"use client"

import { useState, useEffect, Suspense } from "react"
import { useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Switch } from "@/app/components/Switch"
import { GalleryFilters } from "./GalleryFilters"
import { GalleryGrid } from "./GalleryGrid"
import { GalleryModal } from "./GalleryModal"
import { likeImage, unlikeImage } from "@/src/utils/galleryApi"
import type { GalleryListSort } from "@/src/types/gallery"
import { ROOM_TYPE_MAPPINGS, ROOM_THEME_MAPPINGS } from "@/src/utils/api-value-mappings"
import { useGalleryPaged } from "@/hooks/useGalleryPaged"
import { useSession } from "@/app/context/AuthContext"

export type RoomType = "all" | string
export type StyleType = "all" | string
export type TimePeriodType = "all" | "day" | "month"
export type SortType = "created_desc" | "created_asc" | "likes_desc" | "my_likes"

export interface UIGalleryItem {
  id: string
  imageUrl: string
  title: string
  isLiked: boolean
  likeCount: number
  roomType: string
  roomTheme: string
  roomSize?: string
  mood?: string
  intensity?: string
  preferredColors?: string[]
  avoidedColors?: string[]
  accentWall?: boolean
  createdAt: string
  ingestedAt: string
}

function GalleryContent() {
  const { data: session } = useSession()
  const searchParams = useSearchParams()
  const [selectedRoom, setSelectedRoom] = useState<RoomType>("all")
  const [selectedStyle, setSelectedStyle] = useState<StyleType>("all")
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriodType>("all")
  const [selectedSort, setSelectedSort] = useState<SortType>("created_desc")
  const [likedImages, setLikedImages] = useState<Set<string>>(new Set())
  const [likeCountUpdates, setLikeCountUpdates] = useState<Map<string, number>>(new Map())
  const [selectedImage, setSelectedImage] = useState<UIGalleryItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showLoginTooltipForImage, setShowLoginTooltipForImage] = useState<string | null>(null)

  const [isDiscoveryMode, setIsDiscoveryMode] = useState(true)
  const [personalSeed, setPersonalSeed] = useState<string | null>(null)
  const [isUsingPersonalSeed, setIsUsingPersonalSeed] = useState(false)

  const getDailySeed = () => new Date().toISOString().slice(0, 10)
  const generatePersonalSeed = () => `user-${Date.now()}`

  const getCurrentSort = (): GalleryListSort => {
    if (isDiscoveryMode && !hasActiveFilters()) {
      return "random"
    }
    return selectedSort as GalleryListSort
  }

  const getCurrentSeed = (): string | undefined => {
    if (isDiscoveryMode && !hasActiveFilters()) {
      return isUsingPersonalSeed && personalSeed ? personalSeed : getDailySeed()
    }
    return undefined
  }

  const hasActiveFilters = () => {
    return selectedRoom !== "all" || selectedStyle !== "all" || selectedTimePeriod !== "all"
  }

  const viewerUserId = session?.user?.id
  const { items, page, totalPages, total, loading, error, hasPrev, hasNext, setPage, setFilters, setSort, reset } =
    useGalleryPaged(undefined, getCurrentSort(), 12, getCurrentSeed(), viewerUserId)

  useEffect(() => {
    if (items && viewerUserId) {
      const initialLikes = new Set(items.filter((item) => item.liked_by_current_user === true).map((item) => item.id))
      setLikedImages(initialLikes)
    }
  }, [items, viewerUserId])

  useEffect(() => {
    const roomParam = searchParams.get("room") as RoomType
    const styleParam = searchParams.get("style") as StyleType

    if (roomParam && roomParam !== "all") {
      setSelectedRoom(roomParam)
    }
    if (styleParam && styleParam !== "all") {
      setSelectedStyle(styleParam)
    }
  }, [searchParams])

  useEffect(() => {
    const filters: any = {}

    if (selectedRoom !== "all") {
      const roomMapping = ROOM_TYPE_MAPPINGS.find((r) => r.value === selectedRoom || r.name === selectedRoom)
      if (roomMapping) {
        filters.roomType = roomMapping.value
      }
    }

    if (selectedStyle !== "all") {
      const styleMapping = ROOM_THEME_MAPPINGS.find((s) => s.value === selectedStyle || s.name === selectedStyle)
      if (styleMapping) {
        filters.roomTheme = styleMapping.value
      }
    }

    if (selectedTimePeriod !== "all") {
      filters.createdRange = selectedTimePeriod
    }

    if (selectedSort === "my_likes" && viewerUserId) {
      filters.likedByUserId = viewerUserId
    }

    setFilters(Object.keys(filters).length > 0 ? filters : undefined)
    setSort(selectedSort === "my_likes" ? "created_desc" : (getCurrentSort() as GalleryListSort))
    reset()
  }, [
    selectedRoom,
    selectedStyle,
    selectedTimePeriod,
    selectedSort,
    isDiscoveryMode,
    personalSeed,
    isUsingPersonalSeed,
    viewerUserId,
  ])

  const uiItems: UIGalleryItem[] = items.map((item) => {
    const roomMapping = ROOM_TYPE_MAPPINGS.find((r) => r.value === item.roomType)
    const themeMapping = ROOM_THEME_MAPPINGS.find((t) => t.value === item.roomTheme)
    const title = `${themeMapping?.name || item.roomTheme} ${roomMapping?.name || item.roomType}`

    const likeCountUpdate = likeCountUpdates.get(item.id) || 0
    const adjustedLikeCount = Math.max(0, item.likeCount + likeCountUpdate)

    return {
      id: item.id,
      imageUrl: item.imageUrl,
      title,
      isLiked: likedImages.has(item.id),
      likeCount: adjustedLikeCount,
      roomType: item.roomType,
      roomTheme: item.roomTheme,
      roomSize: item.roomSize,
      mood: item.mood,
      intensity: item.intensity,
      preferredColors: item.preferredColors,
      avoidedColors: item.avoidColors,
      accentWall: false,
      createdAt: item.createdAt.toString(),
      ingestedAt: item.ingestedAt.toString(),
    }
  })

  const handleLikeToggle = async (itemId: string) => {
    if (!session?.user?.id) {
      console.log("[v0] User not authenticated, cannot like image")
      setShowLoginTooltipForImage(itemId)
      setTimeout(() => {
        setShowLoginTooltipForImage(null)
      }, 5000)
      return
    }

    console.log("[v0] handleLikeToggle called for item:", itemId)
    const userId = session.user.id
    const item = uiItems.find((i) => i.id === itemId)
    if (!item) {
      console.log("[v0] Item not found:", itemId)
      return
    }

    const wasLiked = item.isLiked
    console.log("[v0] Item found, wasLiked:", wasLiked)

    setLikedImages((prev) => {
      const newSet = new Set(prev)
      if (wasLiked) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      console.log("[v0] Updated liked images set:", Array.from(newSet))
      return newSet
    })

    setLikeCountUpdates((prev) => {
      const newMap = new Map(prev)
      const currentUpdate = newMap.get(itemId) || 0
      const newUpdate = wasLiked ? currentUpdate - 1 : currentUpdate + 1
      newMap.set(itemId, newUpdate)
      console.log("[v0] Updated like count for", itemId, "by", wasLiked ? -1 : 1)
      return newMap
    })

    try {
      console.log("[v0] Making API call:", wasLiked ? "unlike" : "like")
      const response = wasLiked ? await unlikeImage(itemId, userId) : await likeImage(itemId, userId)
      console.log("[v0] API response:", response)
      if (!response.success) {
        console.log("[v0] API call failed, reverting state")
        setLikedImages((prev) => {
          const newSet = new Set(prev)
          if (wasLiked) {
            newSet.add(itemId)
          } else {
            newSet.delete(itemId)
          }
          return newSet
        })
        setLikeCountUpdates((prev) => {
          const newMap = new Map(prev)
          const currentUpdate = newMap.get(itemId) || 0
          const revertedUpdate = wasLiked ? currentUpdate + 1 : currentUpdate - 1
          newMap.set(itemId, revertedUpdate)
          return newMap
        })
      }
    } catch (err) {
      console.error("[v0] Like toggle error:", err)
      setLikedImages((prev) => {
        const newSet = new Set(prev)
        if (wasLiked) {
          newSet.add(itemId)
        } else {
          newSet.delete(itemId)
        }
        return newSet
      })
      setLikeCountUpdates((prev) => {
        const newMap = new Map(prev)
        const currentUpdate = newMap.get(itemId) || 0
        const revertedUpdate = wasLiked ? currentUpdate + 1 : currentUpdate - 1
        newMap.set(itemId, revertedUpdate)
        return newMap
      })
    }
  }

  const handleClearFilters = () => {
    setSelectedRoom("all")
    setSelectedStyle("all")
    setSelectedTimePeriod("all")
    setSelectedSort("created_desc")
  }

  const handleDiscoveryModeToggle = (checked: boolean) => {
    setIsDiscoveryMode(checked)
    if (checked) {
      setIsUsingPersonalSeed(false)
      setPersonalSeed(null)
    }
  }

  const handleRediscover = () => {
    const newPersonalSeed = generatePersonalSeed()
    setPersonalSeed(newPersonalSeed)
    setIsUsingPersonalSeed(true)
  }

  const handleBackToDaily = () => {
    setIsUsingPersonalSeed(false)
    setPersonalSeed(null)
  }

  const handleImageClick = (item: UIGalleryItem) => {
    setSelectedImage(item)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }

  const currentSelectedItem = selectedImage
    ? uiItems.find((item) => item.id === selectedImage.id) || selectedImage
    : null

  if (loading) {
    return (
      <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Gallery</h1>
          <p className="text-gray-600">Loading design inspiration...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Gallery</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Gallery</h1>
        <p className="text-gray-600">Explore our design inspiration.</p>
      </div>

      <div className="flex items-center justify-center gap-3 mb-6">
        <span className="text-sm font-medium text-gray-700">Browse Mode</span>
        <Switch checked={isDiscoveryMode} onCheckedChange={handleDiscoveryModeToggle} disabled={hasActiveFilters()} />
        <span className="text-sm font-medium text-gray-700">Discovery Mode</span>
      </div>

      {isDiscoveryMode && !hasActiveFilters() && (
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg border border-blue-200">
            {isUsingPersonalSeed ? (
              <>
                <span className="text-sm text-blue-700 font-medium">Your Personal Mix</span>
                <button onClick={handleBackToDaily} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  Back to Today's
                </button>
                <span className="text-gray-300">|</span>
                <button onClick={handleRediscover} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  Rediscover
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-blue-700 font-medium">Today's Discoveries...</span>
                <button onClick={handleRediscover} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  Rediscover
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {!isDiscoveryMode && (
        <GalleryFilters
          selectedRoom={selectedRoom}
          selectedStyle={selectedStyle}
          selectedTimePeriod={selectedTimePeriod}
          selectedSort={selectedSort}
          onRoomChange={setSelectedRoom}
          onStyleChange={setSelectedStyle}
          onTimePeriodChange={setSelectedTimePeriod}
          onSortChange={setSelectedSort}
          onClearFilters={handleClearFilters}
          isLoggedIn={!!session?.user?.id}
        />
      )}

      <GalleryGrid
        items={uiItems}
        onLikeToggle={handleLikeToggle}
        onImageClick={handleImageClick}
        showLoginTooltipForImage={showLoginTooltipForImage}
      />

      <GalleryModal
        item={currentSelectedItem}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onLikeToggle={handleLikeToggle}
        showLoginTooltipForImage={showLoginTooltipForImage}
      />

      {!loading && !error && uiItems.length > 0 && (
        <div className="space-y-4 mt-8">
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <Button onClick={() => setPage(page - 1)} variant="outline" disabled={!hasPrev}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button onClick={() => setPage(page + 1)} variant="outline" disabled={!hasNext}>
                Next
              </Button>
            </div>
          )}

          <div className="text-center">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * 12 + 1} to {Math.min(page * 12, total)} of {total} images
            </div>
          </div>
        </div>
      )}

      {!loading && !error && uiItems.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No results found. Try removing some filters.</p>
          <Button onClick={handleClearFilters} variant="outline">
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  )
}

function GalleryFallback() {
  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Loading Gallery...</h1>
        <p className="text-gray-600">Preparing design inspiration for you</p>
      </div>
    </div>
  )
}

export function GalleryPage() {
  return (
    <Suspense fallback={<GalleryFallback />}>
      <GalleryContent />
    </Suspense>
  )
}
